/**
 * ConflictModal
 *
 * When a sync_queue row hits HTTP 409, it's parked in `conflict` status with
 * the server's snapshot stashed in `server_snapshot`. This modal renders one
 * conflict at a time (oldest first) showing local vs server side-by-side and
 * lets the doctor pick "Keep Mine" or "Use Server Version".
 *
 * Mounted once at the app root — opens automatically whenever there's a
 * pending conflict, closes when the queue drains.
 *
 * Field rendering is generic: we don't know the entity shape statically, so
 * we union local + server keys and stringify primitives. PHI never leaves
 * the device — we only show what was already in IndexedDB locally.
 */

import React from "react";

import { useConflicts } from "../../hooks/useConflicts";
import { decrypt } from "../../db/crypto";
import { Button } from "../ui/Button";

const ENTITY_LABELS: Record<string, string> = {
  patient:      "Patient",
  visit:        "Visit",
  vitals:       "Vitals",
  prescription: "Prescription",
  appointment:  "Appointment",
  allergy:      "Allergy",
  doctor:       "Doctor",
};

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return "[unserialisable]"; }
}

// Flatten nested table-grouped payload ({ patients: {...}, patient_contacts: {...} })
// into a single key→value map for display.
function flattenPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (k === "version") continue;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(flat, v as Record<string, unknown>);
    }
  }
  return flat;
}

// Flatten the server 409 snapshot. Unlike local payloads (which are purely
// table-grouped objects), the server response mixes top-level primitives
// (patient_id, first_name, contact_number) with nested sub-objects
// (contact: { aadhar_number, ... }, address: { city, state, ... }).
// This hoists nested sub-object fields up while keeping top-level primitives.
function flattenServerSnapshot(snapshot: Record<string, unknown>): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(snapshot)) {
    if (k === "version") continue;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      Object.assign(flat, v as Record<string, unknown>);
    } else {
      flat[k] = v;
    }
  }
  return flat;
}

export const ConflictModal: React.FC = () => {
  const { conflicts, current, keepMine, useServerVersion } = useConflicts();
  const [busy, setBusy] = React.useState(false);
  const [localData, setLocalData] = React.useState<Record<string, unknown>>({});

  React.useEffect(() => {
    if (!current?.encrypted_data) { setLocalData({}); return; }
    decrypt(current.encrypted_data)
      .then(json => {
        const parsed = JSON.parse(json) as Record<string, unknown>;
        setLocalData(flattenPayload(parsed));
      })
      .catch(() => setLocalData({}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]); // encrypted_data intentionally omitted — re-decrypt only when conflict item changes

  if (!current) return null;

  const local  = localData;
  const server = flattenServerSnapshot(current.server_snapshot ?? {});

  // Only show fields the user actually changed locally.
  // If local is empty (no decrypt yet), fall back to server keys so the modal
  // isn't blank while decryption is in-flight.
  const localKeys = Object.keys(local).filter(k => local[k] != null);
  const allKeys = (localKeys.length > 0 ? localKeys : Object.keys(server)).sort();

  const handle = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              Sync conflict — {ENTITY_LABELS[current.entity_type] ?? current.entity_type}
            </h2>
            {conflicts.length > 1 && (
              <span className="text-xs text-slate-500">
                1 of {conflicts.length}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600">
            The server has a newer version of this record. Choose which to keep.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="font-medium text-slate-700">Your version (local)</div>
            <div className="font-medium text-slate-700">Server version</div>
          </div>
          <div className="mt-2 divide-y divide-slate-100">
            {allKeys.length === 0 && (
              <p className="py-3 text-sm text-slate-500">
                No field-level diff available.
              </p>
            )}
            {allKeys.map(key => {
              const lv = (local  as Record<string, unknown>)[key];
              const sv = (server as Record<string, unknown>)[key];
              const differs = renderValue(lv) !== renderValue(sv);
              return (
                <div
                  key={key}
                  className={`grid grid-cols-2 gap-4 py-2 rounded px-1 ${differs ? "bg-amber-50" : ""}`}
                >
                  <div>
                    <div className="text-xs text-slate-500">{key}</div>
                    <div className={`text-sm break-words ${differs ? "text-amber-800 font-semibold" : "text-slate-600"}`}>
                      {renderValue(lv)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">{key}</div>
                    <div className={`text-sm break-words ${differs ? "text-amber-800 font-semibold" : "text-slate-600"}`}>
                      {renderValue(sv)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => handle(useServerVersion)}
          >
            Use server version
          </Button>
          <Button
            variant="primary"
            isLoading={busy}
            onClick={() => handle(keepMine)}
          >
            Keep mine
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConflictModal;
